function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Delta } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import { hasTenantFeature } from "../../functions/tenant-features";
import { RANGES, usd, num, deltaOf, rangeLabelFor, resolveBusinessModel } from "../sales/territory-common";
import { getGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { decodeEntities, categoryLeaf, isInProgressRange, isAdminUser, pct } from "../stores/common";
import { getDealerPerformance, dealerRows, dealerTotals, lapseKey, lapseLabel, lapseTone } from "../sales/sales-api";

/*
 * Dealers: performance roster for the wholesale book
 * (SALES_FEATURES_REVIEW_2026-10-05 N1: "the Stores rail relabelled").
 *
 * One read: GET /v1/sales/dealers/{range} (same shape as
 * /v1/store/performance/{range}, keyed by dealer: roster + book totals +
 * targets-derived attainment/pace when the tenant has a dealer plan) plus
 * ?$groupBy=category for the dealer×category matrix.
 *
 * Reading the numbers (STORE_PERFORMANCE.md §3.7): a completed window leads
 * with ATTAINMENT; an in-progress window leads with PACE and labels
 * attainment "of plan". Fail-soft: until the route lands the page shows a
 * calm "not available yet" line.
 */
const SORTS = [["revenue", "Revenue"], ["paceAttainmentPct", "Pace"], ["attainmentPct", "Attainment"], ["deltaPct", "Δ vs prev"], ["aov", "AOV"], ["orders", "Orders"]];
class DealersView extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "open", s => {
      this.props.history.push(`/Dealers/${this.range()}/dealer/${encodeURIComponent(s.dealerId)}`);
    });
    this.state = {
      data: undefined,
      matrix: null,
      error: false,
      sort: "revenue",
      businessModel: null
    };
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange("this_year");
  }
  componentDidMount() {
    resolveBusinessModel().then(bm => this._alive && this.setState({
      businessModel: bm
    })).catch(() => this._alive && this.setState({
      businessModel: "both"
    }));
    this.load();
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range) return;
      const target = `/Dealers/${range}`;
      if (window.location.pathname === target) {
        this.setState({
          data: undefined,
          matrix: null,
          error: false
        }, () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    if ((prev.params && prev.params.range) !== (this.props.params && this.props.params.range)) {
      this.setState({
        data: undefined,
        matrix: null,
        error: false
      });
      this.load();
    }
  }
  load() {
    const range = this.range();
    getDealerPerformance(range).then(d => this._alive && this.setState({
      data: d === null ? null : d || {}
    })).catch(() => this._alive && this.setState({
      error: true
    }));
    getDealerPerformance(range, {
      $groupBy: "category"
    }).then(m => this._alive && this.setState({
      matrix: m && m.items || []
    })).catch(() => this._alive && this.setState({
      matrix: []
    }));
  }
  render() {
    if (!hasTenantFeature("territoryRep") || this.state.businessModel === "retail") {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Dealer performance is not configured for this workspace. It requires the wholesale sales feeds (dealer accounts and orders)."));
    }
    const {
      data,
      matrix,
      error,
      sort
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (data === undefined) return /*#__PURE__*/React.createElement(Loader, null);
    const range = this.range();
    if (data === null) {
      return /*#__PURE__*/React.createElement("div", {
        className: "territory-view stores-view wa-view"
      }, /*#__PURE__*/React.createElement(PageHeader, {
        title: "Dealers",
        subtitle: `${rangeLabelFor(range, RANGES)} · wholesale sales by dealer from invoiced orders`
      }), /*#__PURE__*/React.createElement("div", {
        className: "wa-grid"
      }, /*#__PURE__*/React.createElement(Card, {
        title: "Dealer performance"
      }, /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, "Dealer performance is not available yet for this workspace. The dealer roster and targets appear here once the rail is enabled."))));
    }
    const dealers = dealerRows(data);
    const totals = dealerTotals(data);
    const hasTargets = totals.hasTargets;
    const inProgress = isInProgressRange(range);
    dealers.sort((a, b) => (Number(b[sort]) || 0) - (Number(a[sort]) || 0));
    const anyLapse = dealers.some(d => lapseKey(d.lapseRisk));

    // Standout chips: derived from the roster, no extra reads.
    const byAov = dealers.slice().sort((a, b) => (Number(b.aov) || 0) - (Number(a.aov) || 0))[0];
    const paceKey = hasTargets ? "paceAttainmentPct" : "deltaPct";
    const byPace = dealers.slice().sort((a, b) => (Number(b[paceKey]) || 0) - (Number(a[paceKey]) || 0));
    const fastest = byPace[0];
    const slowest = byPace[byPace.length - 1];
    const needsAttention = hasTargets && slowest && Number(slowest.paceAttainmentPct) < 100 ? slowest : null;
    const lapsing = anyLapse ? dealers.filter(d => ["at_risk", "lapsing"].includes(lapseKey(d.lapseRisk))).length : 0;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view stores-view dealers-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      title: "Dealers",
      subtitle: `${rangeLabelFor(range, RANGES)} · wholesale sales by dealer from invoiced orders${hasTargets ? inProgress ? " · mid-period: lead with pace to end (run-rate projected to the window end vs plan); attainment is the share of the full plan banked so far" : " · attainment vs the monthly dealer plan" : ""}`,
      chips: [{
        k: "Dealers",
        v: num(dealers.length)
      }, hasTargets ? {
        k: "Targets",
        v: "set"
      } : null, anyLapse ? {
        k: "At risk or lapsing",
        v: num(lapsing)
      } : null],
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.props.history.push(`/Territory/${range}`)
      }, "Territory & Reps"), isAdminUser() ? /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.props.history.push("/Dealers/targets")
      }, hasTargets ? "Edit targets" : "Set targets") : null)
    }), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: hasTargets ? undefined : 4,
      tiles: [{
        l: "Revenue",
        v: usd(totals.revenue)
      }, {
        l: "Orders",
        v: num(totals.orders)
      }, {
        l: "Units",
        v: num(totals.units)
      }, {
        l: "Average order value",
        v: totals.aov != null ? usd(totals.aov) : Number(totals.orders) > 0 ? usd(Number(totals.revenue) / Number(totals.orders)) : "-----"
      }, hasTargets ? {
        l: inProgress ? "Attainment of plan" : "Attainment",
        v: pct(totals.attainmentPct)
      } : null].filter(Boolean)
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Standouts",
      hint: "from this range's roster",
      span: 4
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table customer-facts"
    }, /*#__PURE__*/React.createElement("tbody", null, byAov && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label"
    }, "Best AOV"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
      className: "rep-state-chip fact-chip",
      onClick: () => this.open(byAov)
    }, byAov.name), " ", usd(byAov.aov))), fastest && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label"
    }, hasTargets ? "Fastest pace" : "Fastest growing"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
      className: "rep-state-chip fact-chip",
      onClick: () => this.open(fastest)
    }, fastest.name), " ", hasTargets ? pct(fastest.paceAttainmentPct) : pct(fastest.deltaPct))), needsAttention && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label"
    }, "Needs attention"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
      className: "rep-state-chip fact-chip",
      onClick: () => this.open(needsAttention)
    }, needsAttention.name), " ", pct(needsAttention.paceAttainmentPct), " pace"))))), /*#__PURE__*/React.createElement(Card, {
      title: "Dealer roster",
      hint: `${num(dealers.length)} dealers · click a dealer for its detail`,
      span: 8,
      right: /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, SORTS.filter(([k]) => hasTargets || k !== "attainmentPct" && k !== "paceAttainmentPct").map(([k, label]) => /*#__PURE__*/React.createElement("button", {
        key: k,
        className: sort === k ? "on" : "",
        onClick: () => this.setState({
          sort: k
        })
      }, label)))
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "#"), /*#__PURE__*/React.createElement("th", null, "Dealer"), anyLapse && /*#__PURE__*/React.createElement("th", null, "Health"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"), hasTargets && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Plan"), hasTargets && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, inProgress ? "Attain (of plan)" : "Attainment"), hasTargets && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Pace \u2192 end"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "\u0394 vs prev"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "AOV"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Share"))), /*#__PURE__*/React.createElement("tbody", null, dealers.map((s, i) => {
      const d = deltaOf(s.revenue, s.revenuePrev);
      const lk = lapseKey(s.lapseRisk);
      const loc = [s.city, s.region].filter(Boolean).join(", ");
      return /*#__PURE__*/React.createElement("tr", {
        key: s.dealerId || i,
        className: "territory-row",
        style: {
          cursor: "pointer"
        },
        onClick: () => this.open(s)
      }, /*#__PURE__*/React.createElement("td", null, i + 1), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, s.name), s.assignedRepName || s.assignedRepId || loc ? /*#__PURE__*/React.createElement("span", {
        style: {
          marginLeft: 8,
          fontSize: 12,
          color: "var(--plk-text-subtle, #777)"
        }
      }, [loc, s.assignedRepName || s.assignedRepId].filter(Boolean).join(" · ")) : null), anyLapse && /*#__PURE__*/React.createElement("td", null, lk ? /*#__PURE__*/React.createElement(Pill, {
        tone: lapseTone(lk)
      }, lapseLabel(lk)) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "\u2014")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, usd(s.revenue)), hasTargets && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, usd(s.targetRevenue)), hasTargets && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, pct(s.attainmentPct)), hasTargets && /*#__PURE__*/React.createElement("td", {
        className: `n ${Number(s.paceAttainmentPct) >= 100 ? "delta-up" : "delta-down"}`
      }, pct(s.paceAttainmentPct)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, d ? /*#__PURE__*/React.createElement(Delta, {
        d: {
          dir: d.dir,
          pct: d.pct
        }
      }) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "\u2014")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(s.orders)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, usd(s.aov)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, pct(s.sharePct)));
    })))), !dealers.length && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "8px 0 0"
      }
    }, "No dealer orders in this range.")), matrix && matrix.length > 0 && dealers.length > 0 && (() => {
      // dealer×category compare: top 12 categories by book revenue, cells shaded per row.
      const top = dealers.slice(0, 12);
      const cols = top.map(s => s.name);
      const idOf = {};
      top.forEach(s => {
        idOf[s.name] = s.dealerId;
      });
      const cell = (r, c) => Number(r.byStore && r.byStore[idOf[c]] != null ? r.byStore[idOf[c]] : r.byDealer && r.byDealer[idOf[c]] != null ? r.byDealer[idOf[c]] : (r.byStore || r.byDealer || {})[c]) || 0;
      const rows = matrix.map(r => ({
        cat: r.category,
        raw: r,
        total: Object.values(r.byStore || r.byDealer || {}).reduce((a, v) => a + (Number(v) || 0), 0)
      })).sort((a, b) => b.total - a.total).slice(0, 12);
      return /*#__PURE__*/React.createElement(Card, {
        title: "Category mix by dealer",
        hint: `top 12 categories · top ${top.length} dealers by revenue · shading is each row's share across dealers`
      }, /*#__PURE__*/React.createElement("div", {
        className: "tblwrap"
      }, /*#__PURE__*/React.createElement("table", {
        className: "territory-table wa-table"
      }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Category"), cols.map(c => /*#__PURE__*/React.createElement("th", {
        key: c,
        className: "n"
      }, c)))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => {
        const max = Math.max(1, ...cols.map(c => cell(r.raw, c)));
        return /*#__PURE__*/React.createElement("tr", {
          key: r.cat
        }, /*#__PURE__*/React.createElement("td", {
          title: decodeEntities(r.cat)
        }, categoryLeaf(r.cat)), cols.map(c => {
          const v = cell(r.raw, c);
          const heat = v / max;
          return /*#__PURE__*/React.createElement("td", {
            key: c,
            className: "n",
            style: heat > 0 ? {
              background: `rgba(255,99,88,${(0.06 + 0.2 * heat).toFixed(2)})`
            } : undefined
          }, v > 0 ? usd(v) : "—");
        }));
      })))));
    })()));
  }
}
export default withRouter(DealersView);